import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { chatShellActions, useChatShellStore } from './chat-shell.store'
import {
  PENDING_CHAT_GENERATION_KEY,
  computeCanAbortSubmit,
  isChatGenerating,
  resolveAbortControllerKey,
  resolveGenerationUiChatId,
} from './chat-generation-keys'
import { useChatAssistantStream } from './use-chat-assistant-stream'
import { useChatMessagesScroll } from './use-chat-messages-scroll'
import type { ChatMessage, ChatSummary } from '@/features/chats/types/chat'
import type { ChatApprovalMode } from '@laplace/contracts'
import { useBootstrapQuery } from '@/features/bootstrap/api/bootstrap/queries/bootstrap-query'
import { getBootstrapWorkspaceRefs } from '@/features/bootstrap/domain/bootstrap-workspaces'
import { useCreateWorkspaceChatMutation } from '@/features/chats/api/chats/mutations/create-chat-mutation'
import { useDeleteWorkspaceChatMutation } from '@/features/chats/api/chats/mutations/delete-chat-mutation'
import { useSendWorkspaceChatMessageMutation } from '@/features/chats/api/chats/mutations/send-message-mutation'
import { useUpdateChatApprovalModeMutation } from '@/features/chats/api/chats/mutations/update-chat-approval-mode-mutation'
import { useOrganizationAgentsQuery } from '@/features/agents/api/agents-query'
import {
  flattenChatMessagePages,
  useChatMessagesInfiniteQuery,
  useWorkspaceChatsQuery,
} from '@/features/chats/api/chats/queries/chats-query'
import { getApiErrorCode } from '@/shared/api/errors'
import {
  getLastWorkspaceId,
  setLastWorkspaceId,
} from '@/shared/lib/last-workspace'
import { canManageOrganizationAsOwner } from '@/features/organizations/domain/organization-role'
import { useCurrentLang } from '@/shared/i18n/use-current-lang'
import { notificationActions } from '@/shared/notifications/notification-store'

function toErrorMessage(code: string | null, fallback: string): string {
  if (code === 'FORBIDDEN' || code === 'FORBIDDEN_NOT_WORKSPACE_MEMBER') {
    return 'You do not have access to this workspace.'
  }

  if (code === 'CHAT_NOT_FOUND') {
    return 'This chat is no longer available.'
  }

  return fallback
}

function getDraftContextId(workspaceId: string, chatId: string | null): string {
  return chatId === null
    ? `workspace:${workspaceId}:new-chat`
    : `chat:${chatId}`
}

export function resolveSelectedAgentId(
  selectedAgents: Record<string, string | null | undefined>,
  chatContextId: string,
  automationAgentId?: string,
): string | null {
  if (Object.prototype.hasOwnProperty.call(selectedAgents, chatContextId)) {
    return selectedAgents[chatContextId] ?? null
  }

  return automationAgentId ?? null
}

export function useWorkspaceChatShellDomain(
  workspaceId: string,
  currentChatId: string | null,
) {
  const { t } = useTranslation('workspace')
  const navigate = useNavigate()
  const lang = useCurrentLang()
  const bootstrapQuery = useBootstrapQuery()
  const organizationId = bootstrapQuery.data?.organization?.id
  const agentsQuery = useOrganizationAgentsQuery(organizationId)
  const agents = agentsQuery.data ?? []
  const [pendingMessage, setPendingMessage] = useState<string | null>(null)
  const [chatErrorCode, setChatErrorCode] = useState<string | null>(null)
  const [newChatApprovalMode, setNewChatApprovalMode] =
    useState<ChatApprovalMode>('ask')
  const canManageAgents = canManageOrganizationAsOwner(
    bootstrapQuery.data?.organization?.role,
  )
  const store = useChatShellStore()
  // Route is the source of truth for the viewed chat. Do not fall back to
  // store.currentChatId — that stuck New chat on the previous conversation.
  const effectiveChatId = currentChatId
  const chatsQuery = useWorkspaceChatsQuery(workspaceId, workspaceId.length > 0)
  const messagesQuery = useChatMessagesInfiniteQuery(
    workspaceId,
    effectiveChatId ?? '',
    effectiveChatId !== null,
  )
  const createChatMutation = useCreateWorkspaceChatMutation()
  const deleteChatMutation = useDeleteWorkspaceChatMutation()
  const sendMessageMutation = useSendWorkspaceChatMessageMutation()
  const updateApprovalModeMutation = useUpdateChatApprovalModeMutation()

  const workspaces = getBootstrapWorkspaceRefs(bootstrapQuery.data?.workspaces)
  const currentWorkspace =
    workspaces.find((workspace) => workspace.id === workspaceId) ?? null
  const chats = chatsQuery.data ?? []
  const activeChat =
    effectiveChatId === null
      ? null
      : (chats.find((chat) => chat.id === effectiveChatId) ?? null)
  const approvalMode = activeChat?.approvalMode ?? newChatApprovalMode
  const messages = useMemo(
    () => flattenChatMessagePages(messagesQuery.data),
    [messagesQuery.data],
  )
  const generationUiChatId = resolveGenerationUiChatId(
    currentChatId,
    store.abortControllerByChatId,
  )
  const abortControllerKey = resolveAbortControllerKey(
    currentChatId,
    store.abortControllerByChatId,
  )
  const isAssistantTyping =
    generationUiChatId !== null
      ? (store.assistantTypingByChatId[generationUiChatId] ?? false)
      : false
  const hasWorkspaces = workspaces.length > 0
  const hasWorkspaceAccess = currentWorkspace !== null
  const workspaceErrorMessage =
    !hasWorkspaceAccess && hasWorkspaces
      ? 'You do not have access to this workspace.'
      : null
  const messageErrorMessage =
    effectiveChatId && (chatErrorCode || messagesQuery.error)
      ? toErrorMessage(chatErrorCode, 'Unable to load this chat.')
      : null
  const messagesFromStore =
    effectiveChatId !== null
      ? (store.messagesByChatId[effectiveChatId] ?? [])
      : []
  const streamingAssistantMessage =
    generationUiChatId !== null
      ? (store.streamingAssistantMessageByChatId[generationUiChatId] ?? null)
      : null
  const streamingArtifacts =
    generationUiChatId !== null
      ? (store.streamingArtifactsByChatId[generationUiChatId] ?? [])
      : []
  const generationStatus =
    generationUiChatId !== null
      ? (store.generationStatusByChatId[generationUiChatId] ?? null)
      : null
  const generationSteps =
    generationUiChatId !== null
      ? (store.generationStepsByChatId[generationUiChatId] ?? [])
      : []
  const generationAgent =
    generationUiChatId !== null
      ? (store.generationAgentByChatId[generationUiChatId] ?? null)
      : null
  const traceByMessageId = store.traceByMessageId
  const isThinkingCollapsed =
    generationUiChatId !== null
      ? (store.thinkingCollapsedByChatId[generationUiChatId] ?? false)
      : false
  const failedGeneration =
    effectiveChatId !== null
      ? (store.failedGenerationByChatId[effectiveChatId] ?? null)
      : null
  const isMessagesLoading = effectiveChatId !== null && messagesQuery.isLoading
  const draftContextId = getDraftContextId(workspaceId, currentChatId)
  const inputValue = store.draftsByChatContextId[draftContextId] ?? ''
  const selectedAgentId = resolveSelectedAgentId(
    store.selectedAgentIdByChatContextId,
    draftContextId,
    activeChat?.automation?.agentId,
  )
  const generationLockKey = currentChatId ?? PENDING_CHAT_GENERATION_KEY
  const isCurrentChatGenerating =
    isChatGenerating(store, effectiveChatId) ||
    (currentChatId === null &&
      isChatGenerating(store, PENDING_CHAT_GENERATION_KEY))
  const renderedBaseMessages =
    currentChatId !== null && currentChatId === effectiveChatId
      ? messages
      : messagesFromStore
  const canAbortSubmit = computeCanAbortSubmit(
    store.abortControllerByChatId,
    abortControllerKey,
  )
  const scrollContextId = draftContextId

  const {
    messagesContainerRef,
    showScrollToBottom,
    handleMessagesScroll,
    handleScrollToBottom,
    setShouldStickToBottom,
  } = useChatMessagesScroll({
    scrollContextId,
    isMessagesLoading,
    isCurrentChatGenerating,
    isAssistantTyping,
    pendingMessage,
    streamingAssistantMessage,
    messagesLength: messages.length,
    hasNextPage: Boolean(messagesQuery.hasNextPage),
    isFetchingNextPage: messagesQuery.isFetchingNextPage,
    fetchNextPage: messagesQuery.fetchNextPage,
  })

  const { handleSubmit, handleAbortSubmit, handleRetryGeneration } =
    useChatAssistantStream({
      workspaceId,
      currentChatId,
      effectiveChatId,
      agents,
      selectedAgentId,
      approvalMode,
      hasWorkspaceAccess,
      isCurrentChatGenerating,
      streamingAssistantMessage,
      inputValue,
      draftContextId,
      generationLockKey,
      abortControllerKey,
      failedGeneration,
      createChatMutation,
      sendMessageMutation,
      setPendingMessage,
      onChatErrorCode: setChatErrorCode,
      onStickToBottomChange: setShouldStickToBottom,
    })

  useEffect(() => {
    chatShellActions.setBootstrap(bootstrapQuery.data ?? null)
    chatShellActions.setBootstrapLoading(
      bootstrapQuery.isLoading || bootstrapQuery.isFetching,
    )
    chatShellActions.setBootstrapError(
      bootstrapQuery.isError ? 'Unable to load app bootstrap.' : null,
    )
  }, [
    bootstrapQuery.data,
    bootstrapQuery.isError,
    bootstrapQuery.isFetching,
    bootstrapQuery.isLoading,
  ])

  useEffect(() => {
    chatShellActions.setCurrentWorkspaceId(workspaceId || null)
    chatShellActions.setCurrentChatId(currentChatId)
    if (workspaceId.length > 0) {
      setLastWorkspaceId(workspaceId)
    }
  }, [currentChatId, workspaceId])

  useEffect(() => {
    setNewChatApprovalMode('ask')
  }, [currentChatId, workspaceId])

  useEffect(() => {
    if (
      selectedAgentId &&
      agentsQuery.data &&
      !agentsQuery.data.some((agent) => agent.id === selectedAgentId)
    ) {
      chatShellActions.setSelectedAgent(draftContextId, null)
    }
  }, [agentsQuery.data, draftContextId, selectedAgentId])

  useEffect(() => {
    if (workspaceId.length === 0) {
      return
    }

    chatShellActions.setWorkspaceChatsLoading(
      workspaceId,
      chatsQuery.isLoading || chatsQuery.isFetching,
    )
    chatShellActions.setWorkspaceChatsError(
      workspaceId,
      chatsQuery.isError ? 'Unable to load chats.' : null,
    )

    if (chatsQuery.data) {
      chatShellActions.setWorkspaceChats(workspaceId, chatsQuery.data)
    }
  }, [
    chatsQuery.data,
    chatsQuery.isError,
    chatsQuery.isFetching,
    chatsQuery.isLoading,
    workspaceId,
  ])

  useEffect(() => {
    if (!effectiveChatId) {
      return
    }

    chatShellActions.setChatMessagesLoading(
      effectiveChatId,
      messagesQuery.isLoading || messagesQuery.isFetching,
    )

    if (messagesQuery.data) {
      chatShellActions.setChatMessages(effectiveChatId, messages)
    }
  }, [
    effectiveChatId,
    messages,
    messagesQuery.data,
    messagesQuery.isFetching,
    messagesQuery.isLoading,
  ])

  useEffect(() => {
    if (!messagesQuery.error) {
      setChatErrorCode(null)
      if (effectiveChatId) {
        chatShellActions.setChatMessagesError(effectiveChatId, null)
      }
      return
    }

    void (async () => {
      const code = await getApiErrorCode(messagesQuery.error)
      setChatErrorCode(code)
      if (effectiveChatId) {
        chatShellActions.setChatMessagesError(
          effectiveChatId,
          toErrorMessage(code, 'Unable to load this chat.'),
        )
      }
    })()
  }, [effectiveChatId, messagesQuery.error])

  const handleWorkspaceChange = (nextWorkspaceId: string) => {
    if (nextWorkspaceId === workspaceId) {
      return
    }

    setLastWorkspaceId(nextWorkspaceId)
    chatShellActions.setCurrentWorkspaceId(nextWorkspaceId)
    chatShellActions.setCurrentChatId(null)
    navigate({
      to: '/$lang/workspace/$workspaceId',
      params: { lang, workspaceId: nextWorkspaceId },
    })
  }

  const handleOpenChat = (chatId: string) => {
    chatShellActions.setCurrentChatId(chatId)
    navigate({
      to: '/$lang/workspace/$workspaceId/chats/$chatId',
      params: { lang, workspaceId, chatId },
    })
  }

  const handleDeleteChat = async () => {
    if (!currentChatId) {
      return
    }

    await deleteChatMutation.mutateAsync({
      workspaceId,
      chatId: currentChatId,
    })

    chatShellActions.setCurrentChatId(null)
    navigate({
      to: '/$lang/workspace/$workspaceId',
      params: { lang, workspaceId },
      replace: true,
    })
  }

  const renderedMessages: Array<ChatMessage> = [
    ...renderedBaseMessages,
    ...(pendingMessage
      ? [
          {
            id: 'pending-user-message',
            role: 'USER' as const,
            content: pendingMessage,
            createdAt: new Date().toISOString(),
            sourceFreshness: [],
            artifacts: [],
            approvals: [],
          },
        ]
      : []),
    ...(generationUiChatId !== null && streamingAssistantMessage !== null
      ? [
          {
            id: 'streaming-assistant-message',
            role: 'ASSISTANT' as const,
            content: streamingAssistantMessage,
            createdAt: new Date().toISOString(),
            sourceFreshness: [],
            agent: generationAgent,
            artifacts: streamingArtifacts,
            approvals: [],
          },
        ]
      : []),
  ]
  const isSubmitting =
    isCurrentChatGenerating ||
    pendingMessage !== null ||
    streamingAssistantMessage !== null ||
    isAssistantTyping

  return {
    bootstrap: bootstrapQuery.data ?? null,
    isBootstrapLoading: bootstrapQuery.isLoading,
    currentUserName: bootstrapQuery.data?.user.name ?? 'You',
    workspaces,
    currentWorkspace,
    chats,
    activeChat,
    messages: renderedMessages,
    currentChatId: effectiveChatId,
    inputValue,
    agents,
    selectedAgentId,
    approvalMode,
    isApprovalModeUpdating: updateApprovalModeMutation.isPending,
    setApprovalMode: async (nextMode: ChatApprovalMode) => {
      if (!activeChat) {
        setNewChatApprovalMode(nextMode)
        return
      }
      try {
        await updateApprovalModeMutation.mutateAsync({
          workspaceId,
          chatId: activeChat.id,
          approvalMode: nextMode,
        })
      } catch {
        notificationActions.error(t('approvalModeUpdateFailed'))
      }
    },
    setSelectedAgentId: (agentId: string | null) =>
      chatShellActions.setSelectedAgent(draftContextId, agentId),
    canManageAgents,
    isSubmitting,
    canAbortSubmit,
    showScrollToBottom,
    isAssistantTyping,
    generationAgent,
    generationStatus,
    generationSteps,
    traceByMessageId,
    isThinkingCollapsed,
    setThinkingCollapsed: (collapsed: boolean) => {
      if (generationUiChatId !== null) {
        chatShellActions.setThinkingCollapsed(generationUiChatId, collapsed)
      }
    },
    isDeletingChat: deleteChatMutation.isPending,
    isChatsLoading: chatsQuery.isLoading,
    isMessagesLoading,
    isFetchingOlderMessages: messagesQuery.isFetchingNextPage,
    chatsErrorMessage: chatsQuery.isError ? 'Unable to load chats.' : null,
    messageErrorMessage,
    workspaceErrorMessage,
    hasWorkspaces,
    hasWorkspaceAccess,
    messagesContainerRef,
    handleMessagesScroll,
    handleScrollToBottom,
    setInputValue: (value: string) =>
      chatShellActions.setDraft(draftContextId, value),
    handleWorkspaceChange,
    handleOpenChat,
    handleAbortSubmit,
    handleDeleteChat,
    handleSubmit,
    failedGeneration,
    handleRetryGeneration,
  }
}

export function getPreferredWorkspaceRoute(
  workspaces: Array<{ id: string }>,
): string | null {
  if (workspaces.length === 0) {
    return null
  }

  const lastWorkspaceId = getLastWorkspaceId()
  return workspaces.some((workspace) => workspace.id === lastWorkspaceId)
    ? lastWorkspaceId
    : (workspaces[0]?.id ?? null)
}

export function sortChatsByUpdatedAt(
  chats: Array<ChatSummary>,
): Array<ChatSummary> {
  return [...chats].sort(
    (left, right) =>
      new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime(),
  )
}
